"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcWindows, type WindowResult, type WindowType, type WindowFrame, type WindowGlass, type WindowInstall, WINDOW_NAR_ROI_PCT } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const TYPE_OPTIONS = [
  { value: "double_hung", label: "Double-Hung — most common" },
  { value: "single_hung", label: "Single-Hung — lower cost" },
  { value: "sliding",     label: "Sliding / Gliding" },
  { value: "picture",     label: "Picture / Fixed (no opening)" },
  { value: "bow_bay",     label: "Bay or Bow Window" },
];
const FRAME_OPTIONS = [
  { value: "vinyl",      label: "Vinyl — low maintenance, best value" },
  { value: "aluminum",   label: "Aluminum — strong, modern look" },
  { value: "wood",       label: "Wood — classic, paintable" },
  { value: "fiberglass", label: "Fiberglass — premium, most durable" },
];
const GLASS_OPTIONS = [
  { value: "double", label: "Double-Pane — standard" },
  { value: "argon",  label: "Double-Pane + Argon Gas — better insulation" },
  { value: "triple", label: "Triple-Pane — best insulation, cold climates" },
];
const INSTALL_OPTIONS = [
  { value: "retrofit",   label: "Retrofit / Insert (keeps existing frame)" },
  { value: "full_frame", label: "Full Frame Replacement (includes new frame)" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [numWindows, setNumWindows] = useState("");
  const [windowType, setWindowType] = useState<WindowType>("double_hung");
  const [frameType, setFrameType] = useState<WindowFrame>("vinyl");
  const [glassType, setGlassType] = useState<WindowGlass>("double");
  const [installType, setInstallType] = useState<WindowInstall>("retrofit");
  const [result, setResult] = useState<WindowResult | null>(null);

  function handleCalculate() {
    setResult(calcWindows({ numWindows: parseInt(numWindows) || 0, windowType, frameType, glassType, installType }));
  }

  const spec = CALCULATOR_SPECS.windows;

  return (
    <CalculatorShell
      name="Window Replacement Cost Calculator"
      description="Estimate window replacement costs by type, frame material, and glass package — fully installed, with ENERGY STAR context."
      icon="windows"
      category="high-value"
      accentColor="#67e8f9"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Window costs vary by size, floor level, and regional labor. Bay/bow windows may require structural work. Always get multiple quotes from licensed installers."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="num-windows" label="Number of Windows" type="number" min="1" step="1" placeholder="8" value={numWindows} onChange={(e) => { setNumWindows(e.target.value); setResult(null); }} hint="Count all windows to be replaced. Doors are separate." />
          <SelectField id="window-type" label="Window Style" value={windowType} onChange={(e) => { setWindowType(e.target.value as WindowType); setResult(null); }} options={TYPE_OPTIONS} />
          <SelectField id="frame-type" label="Frame Material" value={frameType} onChange={(e) => { setFrameType(e.target.value as WindowFrame); setResult(null); }} options={FRAME_OPTIONS} />
          <SelectField id="glass-type" label="Glass Package" value={glassType} onChange={(e) => { setGlassType(e.target.value as WindowGlass); setResult(null); }} options={GLASS_OPTIONS} hint="Argon gas fills the gap between panes for better thermal performance" />
          <SelectField id="install-type" label="Installation Method" value={installType} onChange={(e) => { setInstallType(e.target.value as WindowInstall); setResult(null); }} options={INSTALL_OPTIONS} hint="Full frame replacement is needed if frames are damaged or rotted" />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Cost per Window" value={fmt(result.costPerWindow)} accent size="md" />
              <ResultCard label="Total Windows" value={numWindows} unit="windows" />
            </div>
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Total Installed Cost Range</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>{numWindows} windows × {fmt(result.costPerWindow)}/window avg · ±20% regional variance</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="NAR Resale ROI" value={`${result.roiPct}%`} highlight sub="(vinyl windows · NAR 2025)" />
              <ResultCard label="Est. Value Added" value={fmt(result.estimatedValueAdded)} highlight sub="At resale" />
            </div>
            <div className="info-box flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
              <span>New windows typically pay back through energy savings in 7–15 years. Look for ENERGY STAR certified products — they save $101–$583/year over single-pane windows (DOE).</span>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Understanding ENERGY STAR Ratings &amp; Climate Zones</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Window energy performance is governed by ENERGY STAR Version 7.0, which divides the US into <strong>{spec.buyingGuide.energyStarZones.value}</strong>. Each zone requires different U-factor and SHGC ratings. The U-factor measures how quickly heat escapes through the window — in the Northern zone, look for a <strong>U-factor of {spec.buyingGuide.uFactorExplained.value.split("/")[0].trim()}</strong>. The SHGC (Solar Heat Gain Coefficient) measures how much solar heat enters — Southern homeowners should prioritize a low SHGC (≤0.25) to reduce cooling loads. Look for the NFRC label on every window you purchase: it shows U-factor, SHGC, Visible Transmittance, and Air Leakage for that specific product. A professional <Link href="/articles/window-replacement-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">vinyl window replacement</Link> recovers <strong>{spec.buyingGuide.narRoiVinyl.value}%</strong> of cost at resale per NAR 2025 data.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/windows_buying_guide.webp" alt="Double-hung vinyl replacement windows with ENERGY STAR certification on a residential home" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/windows_site_prep.webp" alt="Window installer applying self-adhering flashing membrane around window rough opening before retrofit installation" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Proper Flashing &amp; Installation Best Practices</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Improper window flashing is the number one cause of water intrusion damage in residential buildings. Under <strong>ASTM E2112</strong>, a self-adhering flashing membrane must be applied on all four sides of the rough opening before any window is inserted. Pan (sill) flashing directs any water that gets past the window frame outward and away from the wall assembly. When choosing between retrofit and full-frame replacement, full-frame is always required when frames are rotted, out of square, or water-damaged — conditions that a retrofit insert will not fix and will actually conceal from future inspection. Always ask your contractor to provide a written warranty covering both the window product and the installation labor.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.impactRatingRequirement.standardCode}
        sourceName={spec.industryStandards.impactRatingRequirement.sourceName}
        sourceUrl={spec.industryStandards.impactRatingRequirement.sourceUrl}
        paragraphs={[
          <p key="p1">
            Homeowners in coastal Florida and other wind-borne debris regions face strict window requirements that go beyond standard ENERGY STAR certification. Under the <b>Florida Building Code ({spec.industryStandards.impactRatingRequirement.standardCode})</b>, all windows in the High-Velocity Hurricane Zone (HVHZ — Miami-Dade and Broward counties) must carry Miami-Dade Notice of Acceptance (NOA) and be rated to <b>ASTM E1886 and E1996 large missile impact standards</b>. These windows can withstand a 9 lb 2x4 board traveling at 50 feet per second simulating hurricane debris.
          </p>,
          <p key="p2">
            Outside the HVHZ but within wind-borne debris regions (generally coastal areas and areas with basic wind speed exceeding 130 mph per ASCE 7), homes with windows that are not impact-rated must use a secondary protection system such as shutters or plywood panels meeting the FBC requirements. To understand your options, read our comparison of <Link href="/articles/impact-windows-vs-hurricane-shutters-cost/" className="text-[var(--accent)] hover:underline font-semibold">impact windows vs hurricane shutters</Link>. Impact windows typically cost 50–100% more than standard windows, but they eliminate the need for shutters, may reduce homeowners insurance premiums, and are often required as part of a comprehensive <Link href="/articles/2026-hurricane-season-home-prep/" className="text-[var(--accent)] hover:underline font-semibold">hurricane season home prep</Link> before insurers will provide coverage in coastal Florida communities.
          </p>
        ]}
        keyRequirements={[
          "HVHZ installations require windows carrying a Miami-Dade Notice of Acceptance (NOA).",
          "Windows must be rated to ASTM E1886 and E1996 large missile impact standards.",
          "Non-impact windows in wind-borne debris regions require secondary protection (shutters/plywood).",
          "Impact-rated windows must withstand a 9 lb 2x4 board traveling at 50 feet per second."
        ]}
      />

      <FaqSection calculatorId="windows" />
      <RelatedCalculators calculatorId="windows" />
    </CalculatorShell>
  );
}
